import { Chip, Tooltip } from "@mui/material";

import { TriggerDispatchStatus } from "@/shared/domain/triggers";

const STATUS_LABELS: Record<TriggerDispatchStatus, string> = {
  [TriggerDispatchStatus.Claimed]: "In progress",
  [TriggerDispatchStatus.Dispatched]: "Dispatched",
  [TriggerDispatchStatus.Failed]: "Failed",
  [TriggerDispatchStatus.Shadow]: "Shadow",
  [TriggerDispatchStatus.Unknown]: "Unknown",
};

const STATUS_COLORS: Record<
  TriggerDispatchStatus,
  "default" | "info" | "success" | "error" | "warning"
> = {
  [TriggerDispatchStatus.Claimed]: "info",
  [TriggerDispatchStatus.Dispatched]: "success",
  [TriggerDispatchStatus.Failed]: "error",
  [TriggerDispatchStatus.Shadow]: "default",
  [TriggerDispatchStatus.Unknown]: "warning",
};

const STATUS_TITLES: Record<TriggerDispatchStatus, string> = {
  [TriggerDispatchStatus.Claimed]:
    "The trigger claimed this dispatch and the target is about to run.",
  [TriggerDispatchStatus.Dispatched]: "The target ran.",
  [TriggerDispatchStatus.Failed]:
    "The target raised; a redelivered platform event may retry it.",
  [TriggerDispatchStatus.Shadow]:
    "Recorded by the retired shadow evaluation of legacy triggers; nothing ever ran.",
  [TriggerDispatchStatus.Unknown]:
    "The outcome could not be determined; flagged for operational review.",
};

interface DispatchStatusChipProps {
  status: TriggerDispatchStatus;
  /** Extra detail (e.g. the error a failed target raised), shown in the tooltip. */
  detail?: string | null;
}

export function DispatchStatusChip({
  status,
  detail,
}: DispatchStatusChipProps) {
  const title = detail
    ? `${STATUS_TITLES[status]} ${detail}`
    : STATUS_TITLES[status];
  return (
    <Tooltip title={title}>
      <Chip
        label={STATUS_LABELS[status]}
        color={STATUS_COLORS[status]}
        size="small"
        variant="outlined"
        data-cy={`dispatch-status-${status}`}
      />
    </Tooltip>
  );
}
